import { yupResolver } from '@hookform/resolvers/yup'
import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import Button from '../../../../components/Button'
import { Container, Form, Row } from '../../../../components/commons'
import Input from '../../../../components/Input'
import Modal from '../../../../components/Modals/Modal'
import useToast from '../../../../hooks/useToast'
import { api } from '../../../../services/api'

import SelectInput from '../../../../components/Input/SelectInput'
import { GroupRegisterForm, registerSchema } from '../../../../validators/group.schemas'
import { UsersList } from '../../Home'
import { UsersSearchStyles } from '../../Documents/FileDetails/GroupsSearch/styles'
import { ButtonIcon } from '../../../../components/Table/Pagination/styles'
import { FaTrash } from 'react-icons/fa6'
import { Empty } from '../../../../components/Table/styles'

export interface CreateProps {
  reload: () => void
}

export interface ModalHandle {
  openFormModal: (initialData?: GroupRegisterForm) => void
}

type ModalRef = React.ElementRef<typeof Modal>

interface User {
  value: string
  label: string
}

const UserRole = ({ item, handleDelete }: { item: User; handleDelete: (item: User) => void }) => {
  return (
    <>
      <UsersSearchStyles.Content>
        <Row gap={10}>
          <UsersSearchStyles.Name>{item.label}</UsersSearchStyles.Name>
        </Row>
        <Row gap={10}>
          <ButtonIcon type='button' onClick={() => handleDelete(item)}>
            <FaTrash size={20} />
          </ButtonIcon>
        </Row>
      </UsersSearchStyles.Content>
    </>
  )
}

const CreatePlan: React.ForwardRefRenderFunction<ModalHandle, CreateProps> = ({ reload }, ref) => {
  const [loading, setLoading] = useState(false)

  const [initialData, setInitialData] = useState<GroupRegisterForm>({
    name: '',
    role: undefined,
  })

  const [users, setUsers] = useState<User[]>([])

  const toast = useToast()

  const modalRef = useRef<ModalRef>(null)

  const handleOpenModalCreate = () => {
    modalRef.current?.openModal()
  }

  const {
    setValue,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<GroupRegisterForm>({
    defaultValues: initialData,
    resolver: yupResolver(registerSchema),
  })

  const handleSubmitLogin = async (formData: GroupRegisterForm) => {
    try {
      setLoading(true)

      let initialId = initialData?._id

      delete formData.users

      if (initialId) {
        await api.put(`/groups/${initialId}`, formData)
      } else {
        const { data: response } = await api.post('/groups/', formData)
        initialId = response.data._id
      }

      if (!initialData?._id) {
        await Promise.all(
          users.map(async (user) => {
            await api.put(`/groups/${initialId}/user/${user?.value}`)
          }),
        )
      }

      modalRef.current?.closeModal()
      reload()
      reset()

      if (!initialId) {
        toast.successToast('Grupos criado com sucesso !')
      } else {
        toast.successToast('Grupos Atualizado com sucesso !')
      }
    } catch (error) {
      toast.errorToast('Erro ao criar grupo !')
    } finally {
      setLoading(false)
    }
  }

  React.useImperativeHandle(ref, () => ({
    openFormModal(initialData) {
      reset()
      if (initialData) {
        setInitialData(initialData)
        setValue('name', initialData.name)
        setValue('role', initialData.role)
        setUsers(
          initialData.users.map((user: any) => ({
            label: user.name,
            value: user._id,
          })),
        )
      } else {
        setInitialData({
          name: '',
          role: undefined,
        })
        reset()
        setUsers([])
      }
      handleOpenModalCreate()
    },
  }))

  const onChangeSelect = async (item: User) => {
    const findUser = users.find((user) => user.value === item.value)
    if (findUser) {
      return toast.errorToast('Usuario ja selecionado')
    }
    const userAux = [...users]

    setValue('users', undefined)
    userAux.push(item)
    setUsers(userAux)

    if (initialData?._id) {
      await api.put(`/groups/${initialData?._id}/user/${item?.value}`)
      toast.successToast('Usuario removido com sucesso !')
    }
  }

  const handleDelete = async (item: User) => {
    const userAux = users.filter((user) => user.value !== item.value)

    setUsers([...userAux])

    if (initialData?._id) {
      await api.delete(`/groups/${initialData?._id}/user/${item.value}`)
      toast.successToast('Usuario removido com sucesso !')
    }
  }

  return (
    <>
      <Modal title='Cadastrar Novo Grupo' ref={modalRef}>
        <Container>
          <Form onSubmit={handleSubmit(handleSubmitLogin)}>
            <Input
              label='Nome'
              placeholder='Digite o nome'
              error={errors?.name}
              control={control}
              type='text'
              name='name'
            />

            <SelectInput
              label='Permissões'
              placeholder='Escolha uma permissão'
              error={errors?.role}
              control={control}
              options={[
                {
                  label: 'Acesso Total',
                  value: 'all-access',
                },
                {
                  label: 'Visualizador',
                  value: 'visualizer',
                },
                {
                  label: 'Editor',
                  value: 'editor',
                },
              ]}
              name='role'
            />

            <UsersList
              control={control}
              label='Selecionar Usuarios'
              isMulti={false}
              onSelect={onChangeSelect}
            />

            {!users.length && <Empty>Nenhum usuario selecionado</Empty>}
            {users.map((user) => (
              <UserRole item={user} handleDelete={handleDelete} />
            ))}

            <Button
              label='Salvar'
              loading={loading}
              type='submit'
              variantType='block'
              color='primary'
            />
          </Form>
        </Container>
      </Modal>
    </>
  )
}

export default React.forwardRef(CreatePlan)
